/**
 * Erfassungs-Dialog (F9/F10): Die KI stellt Rückfragen und fasst zusammen
 * („So habe ich dich verstanden …“); der Kunde bestätigt oder korrigiert.
 * Styling nach Design-Handoff (Screens 03/03c).
 */

import { useEffect, useRef, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { toast } from "sonner"
import { api, apiError, apiStatus } from "@/services/api"
import { NachrichtText } from "@/components/meldung/NachrichtText"
import type { ChatNachricht } from "@/types"

interface Props {
  reportId: number
  nachrichten: ChatNachricht[]
  onNeueNachrichten: (nachrichten: ChatNachricht[]) => void
  /** true im Fenster-Modus: Chat nutzt die volle Höhe der Spalte */
  hoch?: boolean
  /** Zugriff auf die Eingabezeile — z. B. um nach einem Screenshot hinzuspringen */
  eingabeRef?: React.Ref<HTMLInputElement>
}

export function ErfassungsChat({
  reportId,
  nachrichten,
  onNeueNachrichten,
  hoch = false,
  eingabeRef,
}: Props) {
  const [eingabe, setEingabe] = useState("")
  const [kiVerfuegbar, setKiVerfuegbar] = useState(true)
  // Hans 02.10.2026: Nur der Verlauf scrollt nach unten — NICHT die Seite.
  // Früher scrollIntoView(): das verschob die ganze Seite so, dass das
  // Chat-Ende oben am Bildschirmrand stand, und der Dialog sprang aus dem Bild.
  const verlaufRef = useRef<HTMLDivElement>(null)

  // Optimistische Kunden-Nachricht (negative ID) sofort anzeigen; bei der
  // Antwort durch den gespeicherten Stand ersetzen. `nachrichten` wird beim
  // Aufruf der Callbacks bereits die optimistische Zeile enthalten (die
  // Mutation sieht immer die aktuellen Props) — deshalb vorher herausfiltern,
  // sonst steht die Nachricht doppelt im Strang (beobachtet 28.09.2026).
  const ohneOptimistische = () => nachrichten.filter((n) => n.id > 0)
  const senden = useMutation({
    mutationFn: (inhalt: string) => api.reports.chat(reportId, inhalt),
    onMutate: (inhalt) => {
      onNeueNachrichten([
        ...ohneOptimistische(),
        { id: -Date.now(), rolle: "kunde", inhalt, erstelltAm: new Date().toISOString() },
      ])
      setEingabe("")
    },
    onSuccess: (antwort, inhalt) => {
      onNeueNachrichten([
        ...ohneOptimistische(),
        { id: antwort.id - 1, rolle: "kunde", inhalt, erstelltAm: antwort.erstelltAm },
        antwort,
      ])
    },
    onError: (e, inhalt) => {
      // Optimistische Nachricht zurücknehmen
      onNeueNachrichten(ohneOptimistische())
      setEingabe(inhalt)
      // 503 = kein Provider konfiguriert → Chat ausblenden, manueller Weg bleibt
      if (apiStatus(e) === 503) setKiVerfuegbar(false)
      toast.error(apiError(e))
    },
  })

  useEffect(() => {
    const el = verlaufRef.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" })
  }, [nachrichten.length, senden.isPending])

  // Antwortvorschläge (Hans 09.10.2026): nur unter der LETZTEN KI-Nachricht —
  // ein Klick schickt den Text wie eine getippte Antwort; danach sind die
  // Knöpfe weg, die Antwort steht als Kundennachricht im Verlauf.
  const letzte = nachrichten[nachrichten.length - 1]
  const optionen =
    letzte && letzte.rolle === "ki" && !senden.isPending ? (letzte.optionen ?? []) : []

  if (!kiVerfuegbar) {
    return (
      <p className="rounded-lg bg-warning/10 px-3 py-2 text-xs text-warning">
        Die Erfassungs-KI ist derzeit nicht verfügbar — bitte fülle die Felder manuell aus.
      </p>
    )
  }

  return (
    <div
      className={`flex flex-col overflow-hidden rounded-[10px] border ${
        hoch ? "min-h-0 flex-1" : ""
      }`}
    >
      <div className="flex items-center justify-between border-b bg-card px-3.5 py-2.5">
        <span className="text-[13px] font-bold">Erfassungs-Assistent</span>
        <span className="flex items-center gap-1.5 text-xs font-semibold text-success">
          <span className="h-1.5 w-1.5 rounded-full bg-success" />
          KI aktiv
        </span>
      </div>
      <div
        ref={verlaufRef}
        className={`overflow-y-auto bg-muted/40 p-2.5 ${hoch ? "min-h-0 flex-1" : "max-h-64"}`}
      >
        {nachrichten.length === 0 && (
          <p className="px-1 py-2 text-xs text-muted-foreground">
            Beschreibe kurz dein Anliegen — die KI stellt ein, zwei kurze Rückfragen und
            fasst dann zusammen. <strong>Was du nicht weißt, schreib einfach „weiß ich
            nicht“</strong> — das ist völlig in Ordnung, den Rest klärt netz-fabrik.
          </p>
        )}
        <div className="flex flex-col gap-2">
          {nachrichten.map((n) => (
            <div
              key={n.id}
              className={`max-w-[85%] px-3 py-2 text-[13px] leading-normal ${
                n.rolle === "kunde"
                  ? "self-end rounded-[12px_12px_3px_12px] bg-primary text-primary-foreground"
                  : "self-start rounded-[12px_12px_12px_3px] bg-secondary"
              }`}
            >
              <NachrichtText text={n.inhalt} />
            </div>
          ))}
          {senden.isPending && (
            <div className="self-start rounded-[12px_12px_12px_3px] bg-secondary px-3 py-2 text-[13px] text-muted-foreground">
              KI überlegt …
            </div>
          )}
          {optionen.length > 0 && (
            <div className="flex max-w-[85%] flex-wrap gap-1.5 self-start pl-1">
              {optionen.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => senden.mutate(o)}
                  className="rounded-full border border-primary/40 bg-card px-3 py-1 text-[12.5px] font-semibold text-primary hover:bg-primary hover:text-primary-foreground"
                >
                  {o}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <form
        className="flex gap-2 border-t bg-card p-2"
        onSubmit={(e) => {
          e.preventDefault()
          const inhalt = eingabe.trim()
          if (inhalt && !senden.isPending) senden.mutate(inhalt)
        }}
      >
        <input
          ref={eingabeRef}
          value={eingabe}
          onChange={(e) => setEingabe(e.target.value)}
          placeholder="Deine Antwort … („weiß ich nicht“ ist auch eine Antwort)"
          className="w-full scroll-mb-6 rounded-lg border px-3 py-1.5 text-[13px]"
        />
        <button
          type="submit"
          disabled={!eingabe.trim() || senden.isPending}
          className="shrink-0 rounded-lg bg-primary px-3.5 py-1.5 text-[13px] font-semibold text-primary-foreground disabled:opacity-50"
        >
          Senden
        </button>
      </form>
    </div>
  )
}
